import React, { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import 'dayjs/locale/hu';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { QUOTE_PRODUCTS_HEAD_CELLS } from 'utils/constans/quoteProductsTableHead';

import { BasicTable } from 'components/shared/BasicTable/BasicTable';
import { QuotationNextButton } from '../shared/QuotationNextButton';
import { QuotationProductsTableRow } from './QuotationProductsTableRow';
import MobileQuatationList from './mobile/QuatationList';

import useQuotation from 'hooks/quotations/useQuotation';

import {
    QuotationAccordionProps,
    QuotationProductsForm,
    QuotationProductsUpdateProps,
} from 'models/quotations/Quotation';
import {
    QuotationProductItem,
    QuotationUpdateProductdItem,
} from 'models/quotations/Quotations';

import { deepCompareObjects } from 'utils/deepCompareObjects';
import { QuotationAddNewProductButton } from './QuotationAddNewProductButton';

const TABLE_HEAD_SETTINGS = {
    translateNs: 'quotations',
    translatePrefix:
        'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_PRODUCTS.TABLE_HEAD',
    selectable: false,
    headContent: QUOTE_PRODUCTS_HEAD_CELLS,
};

export const QuotationProducts: React.FC<QuotationAccordionProps> = (props) => {
    const {
        handleNext,
        index,
        quoteData: { id, items },
        updateProducts,
    } = props;

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_PRODUCTS',
    });

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { saveItems, deleteQuotationItem } = useQuotation();

    const form = useForm<QuotationProductsForm>({
        mode: 'onChange',
        defaultValues: { items },
    });

    const { control, reset, handleSubmit } = form;

    const {
        fields: products,
        update,
        remove,
    } = useFieldArray({
        control,
        name: 'items',
        keyName: 'key',
    });

    const onSubmit = (data: QuotationProductsForm, next: boolean): void => {
        // finding the items that were modified
        const modified_items = data.items.filter((item): boolean => {
            const original = items.find((object) => object.id === item.id);
            if (!original) return false;
            return !deepCompareObjects(original, item);
        });

        // if none of the items changed, we do not continue the procedure
        if (modified_items.length < 1) return handleNext(index);

        // creating the payload of the update items function
        const updateables: QuotationUpdateProductdItem[] = modified_items.map(
            (item) => ({
                id: item.id,
                quantity: Number(item.quantity),
                margin: Number(item.margin),
                customs: item.customs.map((custom) => ({
                    id: custom.id,
                    name: custom.name,
                    net_price: Number(custom.net_price),
                    quantity: Number(custom.quantity),
                    tax: Number(custom.tax),
                    unit: custom.unit,
                })),
            })
        );

        saveItems(updateables, id)
            .then(({ items }) => {
                const updated = data.items.map((item) => {
                    const updatedItem = items.find(
                        (element) => element.offer_item.sku === item.sku
                    );

                    if (!updatedItem) return item;

                    return {
                        ...item,
                        ...updatedItem.offer_item,
                        customs: updatedItem.customs,
                    };
                });

                if (next) handleNext(index, { items: updated });
                else updateProducts();
            })
            .catch((error) => console.log(error));
    };

    const handleChange = ({ index, item }: QuotationProductsUpdateProps) => {
        update(index, { ...products[index], ...item });
    };

    const handleDelete = useCallback(
        (props: { index: number; quoteItemId: number }): void => {
            deleteQuotationItem(props.quoteItemId)
                .then(() => remove(props.index))
                .catch((error) => console.log(error));
        },
        []
    );

    let submitRef = useRef<HTMLFormElement>();

    const handleUpdateProducts = () => {
        /*submitRef.current.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
        );*/
    };

    useEffect((): void => reset({ items }), [items]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit((d) => {
                    onSubmit(d, false);
                })}
                ref={submitRef}
            >
                {isMobile ? (
                    <MobileQuatationList
                        items={products}
                        handleChange={handleChange}
                        handleEvent={handleDelete}
                    />
                ) : (
                    <>
                        <Typography
                            variant="subtitle2"
                            sx={{ margin: '12px 0' }}
                        >
                            {t('TITLE')}
                        </Typography>
                        <BasicTable<QuotationProductItem, QuotationProductItem>
                            fullList={products}
                            tableHeadSettings={TABLE_HEAD_SETTINGS}
                            component={QuotationProductsTableRow}
                            handleChange={handleChange}
                            handleEvent={handleDelete}
                        />
                    </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row' }}>
                    <QuotationAddNewProductButton
                        quotationId={id}
                        handleUpdateProducts={handleUpdateProducts}
                    />
                    <QuotationNextButton
                        fullWidth={isMobile}
                        onClick={handleSubmit((d) => {
                            onSubmit(d, true);
                        })}
                    />
                </Box>
            </form>
        </FormProvider>
    );
};
